import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Search, CheckCircle, AlertTriangle, Building2 } from 'lucide-react';
import axios from 'axios';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

const ReturnBook = () => {
  const [issuedBooks, setIssuedBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [finePerDay, setFinePerDay] = useState(50);
  const [banks, setBanks] = useState([]);
  const [returnData, setReturnData] = useState({
    fine_amount: 0,
    payment_mode: 'Cash',
    bank_id: '',
    bank_name: '',
    return_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchIssuedBooks();
    fetchFineSetting();
    fetchBanks();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = issuedBooks.filter(issue =>
        issue.book_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.member_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.isbn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.accession_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredBooks(filtered);
    } else {
      setFilteredBooks(issuedBooks);
    }
  }, [searchTerm, issuedBooks]);

  const fetchIssuedBooks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/library/issues?status=Issued`, { headers: getAuthHeaders() });
      const data = Array.isArray(response.data) ? response.data : [];
      setIssuedBooks(data);
      setFilteredBooks(data);
    } catch (error) {
      console.error('Error fetching issued books:', error);
      setIssuedBooks([]);
      setFilteredBooks([]);
    }
  };

  const fetchFineSetting = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/fine-settings`, { headers: getAuthHeaders() });
      const fineSettings = Array.isArray(response.data) ? response.data : [];
      const libraryFine = fineSettings.find(
        f => f.fine_type === 'Library Fine' && f.calculation_method === 'Per Day' && f.is_active
      );
      if (libraryFine) {
        setFinePerDay(libraryFine.amount);
      }
    } catch (error) {
      console.error('Error fetching fine settings:', error);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/banks`, { headers: getAuthHeaders() });
      const bankList = Array.isArray(response.data) ? response.data : [];
      setBanks(bankList);
    } catch (error) {
      console.error('Error fetching banks:', error);
      setBanks([]);
    }
  };

  const handleReturnClick = (issue) => {
    setSelectedIssue(issue);
    const fine = calculateFine(issue.due_date);
    setReturnData({ fine_amount: fine, payment_mode: 'Cash', bank_id: '', bank_name: '', return_date: new Date().toISOString().split('T')[0] });
    setIsModalOpen(true);
  };

  const calculateFine = (dueDate) => {
    if (!dueDate) return 0;
    const due = new Date(dueDate);
    const today = new Date();
    const diffDays = Math.ceil((today - due) / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays * finePerDay : 0;
  };

  const calculateOverdueDays = (dueDate) => {
    if (!dueDate) return 0;
    const due = new Date(dueDate);
    const today = new Date();
    return Math.max(0, Math.ceil((today - due) / (1000 * 60 * 60 * 24)));
  };

  const handleBankSelect = (e) => {
    const bankId = e.target.value;
    const bank = banks.find(b => b.id === bankId);
    setReturnData({ ...returnData, bank_id: bankId, bank_name: bank ? bank.bank_name : '' });
  };

  const handleReturn = async (e) => {
    e.preventDefault();
    if (returnData.fine_amount > 0 && returnData.payment_mode !== 'Cash' && !returnData.bank_id) {
      alert('Please select a bank for non-cash payment');
      return;
    }
    setLoading(true);
    try {
      let url = `${API_URL}/api/library/issues/${selectedIssue.id}/return?fine_amount=${returnData.fine_amount}&payment_mode=${returnData.payment_mode}&return_date=${returnData.return_date}`;
      if (returnData.bank_id) {
        url += `&bank_id=${returnData.bank_id}&bank_name=${encodeURIComponent(returnData.bank_name)}`;
      }
      await axios.put(url, {}, { headers: getAuthHeaders() });
      const copyInfo = selectedIssue.accession_number ? ` (${selectedIssue.accession_number})` : '';
      alert(`Book returned successfully!${copyInfo}${returnData.fine_amount > 0 ? ` Fine ₹${returnData.fine_amount} collected via ${returnData.payment_mode}${returnData.bank_name ? ` (${returnData.bank_name})` : ''}.` : ''}`);
      fetchIssuedBooks();
      handleCloseModal();
    } catch (error) {
      console.error('Error returning book:', error);
      alert(error.response?.data?.detail || 'Failed to return book');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedIssue(null);
    setReturnData({ fine_amount: 0, payment_mode: 'Cash', bank_id: '', bank_name: '', return_date: new Date().toISOString().split('T')[0] });
  };

  // Bug fix: removed shadowing formatDate – using imported one

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const isBankPayment = returnData.payment_mode !== 'Cash';

  return (
    <div className="p-6 space-y-6" data-testid="return-book-page">
      {/* Fine Rate Info */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 text-yellow-600" />
        <div>
          <span className="font-medium text-yellow-800">Current Library Fine Rate: </span>
          <span className="text-yellow-700">₹{finePerDay} per day</span>
          <span className="text-yellow-600 text-sm ml-2">(Configured in Master Settings)</span>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Return Book</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search by book title, member name, ISBN, or accession number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="return-search-input"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="return-book-table">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 text-sm font-semibold">Book Title</th>
                  <th className="text-left p-3 text-sm font-semibold">Accession No</th>
                  <th className="text-left p-3 text-sm font-semibold">Member</th>
                  <th className="text-left p-3 text-sm font-semibold">Issue Note</th>
                  <th className="text-left p-3 text-sm font-semibold">Issue Date</th>
                  <th className="text-left p-3 text-sm font-semibold">Due Date</th>
                  <th className="text-left p-3 text-sm font-semibold">Overdue</th>
                  <th className="text-left p-3 text-sm font-semibold">Fine</th>
                  <th className="text-left p-3 text-sm font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredBooks.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center p-4 text-gray-500">No issued books found.</td>
                  </tr>
                ) : (
                  filteredBooks.map((issue) => {
                    const overdueDays = calculateOverdueDays(issue.due_date);
                    const fine = calculateFine(issue.due_date);
                    const overdue = isOverdue(issue.due_date);

                    return (
                      <tr key={issue.id} className={`border-b hover:bg-gray-50 ${overdue ? 'bg-red-50' : ''}`}>
                        <td className="p-3 font-medium">{issue.book_title || 'N/A'}</td>
                        <td className="p-3">
                          {issue.accession_number ? (
                            <span className="font-mono text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium">
                              {issue.accession_number}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="p-3">{issue.member_name || 'N/A'}</td>
                        <td className="p-3">
                          {issue.issue_description ? (
                            <span className="text-xs text-gray-700 bg-yellow-50 border border-yellow-200 px-2 py-1 rounded max-w-[200px] truncate block" title={issue.issue_description}>
                              {issue.issue_description}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="p-3 text-sm">{formatDate(issue.issue_date)}</td>
                        <td className="p-3 text-sm">
                          <span className={overdue ? 'text-red-600 font-medium' : ''}>{formatDate(issue.due_date)}</span>
                        </td>
                        <td className="p-3">
                          {overdue ? (
                            <span className="text-red-600 font-medium">{overdueDays} days</span>
                          ) : (
                            <span className="text-green-600 text-sm">On time</span>
                          )}
                        </td>
                        <td className="p-3">
                          <span className={fine > 0 ? 'text-red-600 font-bold' : 'text-green-600'}>₹{fine}</span>
                        </td>
                        <td className="p-3">
                          <Button size="sm" onClick={() => handleReturnClick(issue)} className="flex items-center gap-1"
                            data-testid={`return-btn-${issue.id}`}>
                            <CheckCircle className="w-3 h-3" /> Return
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">Return Book</DialogTitle>
          </DialogHeader>
          {selectedIssue && (
            <form onSubmit={handleReturn} className="space-y-3">
              <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
                <p><strong>Book:</strong> {selectedIssue.book_title}</p>
                {selectedIssue.accession_number && (
                  <p><strong>Accession No:</strong>
                    <span className="font-mono bg-blue-50 text-blue-700 px-2 py-0.5 rounded ml-1 text-xs">
                      {selectedIssue.accession_number}
                    </span>
                  </p>
                )}
                <p><strong>Member:</strong> {selectedIssue.member_name}</p>
                <div className="flex gap-4">
                  <p><strong>Issue:</strong> {formatDate(selectedIssue.issue_date)}</p>
                  <p><strong>Due:</strong> {formatDate(selectedIssue.due_date)}</p>
                </div>
                {calculateOverdueDays(selectedIssue.due_date) > 0 && (
                  <p className="text-red-600"><strong>Overdue:</strong> {calculateOverdueDays(selectedIssue.due_date)} days</p>
                )}
              </div>

              {/* Issue Description - prominently displayed for return verification */}
              {selectedIssue.issue_description && (
                <div className="bg-yellow-50 border border-yellow-300 p-3 rounded-lg">
                  <p className="text-sm font-semibold text-yellow-800 mb-1">Issue Note:</p>
                  <p className="text-sm text-yellow-900 whitespace-pre-wrap">{selectedIssue.issue_description}</p>
                </div>
              )}

              <div>
                <Label className="text-sm">Return Date</Label>
                <Input type="date" value={returnData.return_date} onChange={(e) => setReturnData(prev => ({ ...prev, return_date: e.target.value }))} max={new Date().toISOString().split('T')[0]} data-testid="return-date" />
              </div>

              {returnData.fine_amount > 0 && (
                <>
                  <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div><span className="text-red-700">Overdue:</span> <strong>{calculateOverdueDays(selectedIssue.due_date)} days</strong></div>
                      <div><span className="text-red-700">Rate:</span> <strong>₹{finePerDay}/day</strong></div>
                      <div><span className="text-red-700">Total:</span> <strong className="text-lg">₹{returnData.fine_amount}</strong></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Fine Amount</Label>
                      <Input type="number" step="0.01" min="0" value={returnData.fine_amount}
                        onChange={(e) => setReturnData({ ...returnData, fine_amount: parseFloat(e.target.value) || 0 })}
                        className="h-9" />
                    </div>
                    <div>
                      <Label className="text-sm">Payment Mode</Label>
                      <select value={returnData.payment_mode}
                        onChange={(e) => setReturnData({ ...returnData, payment_mode: e.target.value, bank_id: '', bank_name: '' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm h-9">
                        <option value="Cash">Cash</option>
                        <option value="Bank">Bank Transfer</option>
                        <option value="UPI">UPI</option>
                        <option value="Card">Card</option>
                      </select>
                    </div>
                  </div>

                  {isBankPayment && (
                    <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                      <Label className="flex items-center gap-2 text-blue-800 text-sm">
                        <Building2 className="w-4 h-4" /> Select Bank *
                      </Label>
                      <select required value={returnData.bank_id} onChange={handleBankSelect}
                        className="w-full mt-1 px-3 py-2 border border-blue-300 rounded-lg text-sm">
                        <option value="">-- Select Bank --</option>
                        {banks.map((bank) => (
                          <option key={bank.id} value={bank.id}>
                            {bank.bank_name} (₹{bank.current_balance?.toLocaleString('en-IN') || 0})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}

              <DialogFooter className="pt-2">
                <Button type="button" variant="outline" size="sm" onClick={handleCloseModal}>Cancel</Button>
                <Button type="submit" size="sm" disabled={loading} data-testid="confirm-return-btn">
                  {loading ? 'Processing...' : returnData.fine_amount > 0 ? `Return & Collect ₹${returnData.fine_amount}` : 'Confirm Return'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReturnBook;
